/**
 * "Upload history (→ Dec 2025)" — the Fees page's dedicated HISTORICAL-fee upload
 * (fee decoupling B-hist, 2026-07-11). A 1-sheet "Fees ex-VAT" CIIF-columned workbook →
 * agreement_fee_history, INDEPENDENT of the AuM/returns history upload (which lives on the
 * AuM and Returns page). Select → Review → Done, mirroring the bulk metrics upload. Admin-only
 * (the endpoint gates; the tab is on Admin → Fees).
 */
import { useState } from 'react';
import { Alert, Button, Card, Group, List, Modal, Stack, Stepper, Text, Title, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconAlertTriangle, IconCheck, IconDownload } from '@tabler/icons-react';

import { feeHistoryImportApi } from '../api/feeHistoryImport';
import type { MetricsImportConflict, MetricsImportReport } from '../api/monthlyMetrics';
import { XlsxDropzone } from './XlsxDropzone';

const plural = (n: number) => (n === 1 ? '' : 's');

function toastErr(title: string, err: unknown) {
  notifications.show({
    color: 'red', title,
    message: err instanceof Error ? err.message : 'Something went wrong.',
  });
}

export function FeeHistoryUploadSection() {
  const [downloading, setDownloading] = useState(false);
  const [step, setStep] = useState(0);                // 0 Select · 1 Review · 2 Done
  const [file, setFile] = useState<File | null>(null); // RETAINED for commit re-send
  const [preview, setPreview] = useState<MetricsImportReport | null>(null);
  const [result, setResult] = useState<MetricsImportReport | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [confirm, setConfirm] = useState<MetricsImportConflict | null>(null);  // commit-confirm modal

  function reset() {
    setStep(0); setFile(null); setPreview(null); setResult(null);
    setPreviewing(false); setCommitting(false); setConfirm(null);
  }

  async function handleDownload() {
    setDownloading(true);
    try { await feeHistoryImportApi.downloadTemplate(); }
    catch (err) { toastErr('Template download failed', err); }
    finally { setDownloading(false); }
  }

  async function handleSelect(picked: File) {
    setFile(picked); setPreviewing(true);
    try {
      const rep = await feeHistoryImportApi.importFile(picked, 'preview', 'skip');
      setPreview(rep); setStep(1);
    } catch (err) { setFile(null); toastErr('Could not read the workbook', err); }
    finally { setPreviewing(false); }
  }

  async function handleCommit(onConflict: MetricsImportConflict) {
    if (!file) return;
    setCommitting(true);
    try {
      const rep = await feeHistoryImportApi.importFile(file, 'commit', onConflict);
      setResult(rep); setStep(2); setConfirm(null);
    } catch (err) { toastErr('Import failed', err); }
    finally { setCommitting(false); }
  }

  return (
    <Card withBorder padding="lg">
      <Stack>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Title order={4}>Upload fee history (before 2026)</Title>
            <Text c="dimmed" size="sm">
              Upload the firm&apos;s historical fee actuals (ex-VAT) — one &quot;Fees ex-VAT&quot;
              sheet, one row per agreement (CIIF down column A), one column per month (month-end
              dates across the top). History only: months from Jan-2026 onward are computed in-app
              and are rejected here. A negative fee (credit note) is allowed.
            </Text>
          </div>
          <Button variant="light" leftSection={<IconDownload size={16} />} onClick={handleDownload}
            loading={downloading} style={{ flexShrink: 0 }}>
            Download blank template
          </Button>
        </Group>

        <Stepper active={step}>
          <Stepper.Step label="Select" description="Choose the workbook">
            <Stack pt="md">
              <XlsxDropzone
                hint="One Excel file: the fee-history workbook (the blank template, filled in)."
                onSelect={handleSelect}
                loading={previewing}
                onReject={() => notifications.show({
                  color: 'red', title: 'Not accepted', message: 'Please choose a single .xlsx workbook.',
                })}
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Review" description="Check the reconciliation">
            <Stack pt="md">
              {preview && (
                <Stack gap="sm">
                  {file?.name && <Text size="sm" c="dimmed">{file.name}</Text>}
                  <Text>
                    <b>{preview.matched_ciifs.length}</b> agreement{plural(preview.matched_ciifs.length)} matched ·{' '}
                    <b>{preview.new_count}</b> new month-row{plural(preview.new_count)} ·{' '}
                    <b>{preview.conflict_count}</b> already present ·{' '}
                    <b>{preview.unmatched_ciifs.length}</b> unmatched ·{' '}
                    <b>{preview.errors.length}</b> error{plural(preview.errors.length)}
                  </Text>

                  {preview.unmatched_ciifs.length > 0 && (
                    <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}
                      title={`${preview.unmatched_ciifs.length} CIIF code${plural(preview.unmatched_ciifs.length)} matched no live agreement — ignored`}>
                      <Text size="sm">{preview.unmatched_ciifs.join(', ')}</Text>
                    </Alert>
                  )}

                  {preview.errors.length > 0 && (
                    <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}
                      title={`${preview.errors.length} row${plural(preview.errors.length)} skipped (incl. any Jan-2026+ rows — computed in-app)`}>
                      <List size="sm" spacing={2}>
                        {preview.errors.slice(0, 50).map((e, i) => <List.Item key={i}>{e}</List.Item>)}
                      </List>
                    </Alert>
                  )}

                  {(preview.delete_count ?? 0) > 0 && (
                    <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}
                      title={`${preview.delete_count} stored fee-month${plural(preview.delete_count)} not in this file`}>
                      <Text size="sm" c="dimmed">
                        These historical fee months exist in the app but not in your upload. <b>Overwrite</b> and{' '}
                        <b>Import new</b> leave them untouched; <b>Replace history</b> deletes them so the file
                        becomes the complete fee record for its agreements.
                      </Text>
                      <Text size="sm" mt={4} c="dimmed">
                        Showing first {preview.delete_sample.length} of {preview.delete_count}:
                      </Text>
                      <Text size="sm">{preview.delete_sample.join('  ·  ')}</Text>
                    </Alert>
                  )}

                  <ConfirmActions report={preview} committing={committing} onConfirm={setConfirm} onCancel={reset} />
                </Stack>
              )}
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Done" description="Result">
            <Stack pt="md">
              {result && (
                <Alert color="green" variant="light" icon={<IconCheck size={16} />} title="Import complete">
                  <Text size="sm">
                    Imported <b>{result.inserted}</b> new month{plural(result.inserted)}.
                    {result.overwritten > 0 && <> Overwrote <b>{result.overwritten}</b> existing.</>}
                    {result.skipped > 0 && <> Kept <b>{result.skipped}</b> existing (skipped).</>}
                    {(result.deleted ?? 0) > 0 && <> Deleted <b>{result.deleted}</b> fee-month{plural(result.deleted)} not in the file.</>}
                  </Text>
                </Alert>
              )}
              <Group><Button variant="light" onClick={reset}>Upload another file</Button></Group>
            </Stack>
          </Stepper.Step>
        </Stepper>

        {/* Commit confirm (§5) + overwrite challenge (§6) — fee history is multi-month, so this
            names the SCOPE (counts) rather than a single month. */}
        <Modal
          opened={confirm !== null} onClose={() => setConfirm(null)}
          title={
            confirm === 'replace' ? 'Replace fee history — confirm deletion'
              : confirm === 'overwrite' ? 'Overwrite existing fee history?'
                : 'Import historical fees?'
          }
        >
          {preview && confirm && (
            <Stack gap="sm">
              {confirm === 'replace' ? (
                <>
                  <Text size="sm">
                    This makes the uploaded file the <b>complete fee-history record</b> for its agreements:
                    overwrite <b>{preview.conflict_count}</b>, add <b>{preview.new_count}</b>, and{' '}
                    <Text span c="red" fw={700}>
                      permanently delete {preview.delete_count} stored fee-month{plural(preview.delete_count)}
                    </Text>{' '}
                    not in the file.
                  </Text>
                  {preview.delete_sample.length > 0 && (
                    <Text size="xs" c="dimmed">
                      Will delete (first {preview.delete_sample.length} of {preview.delete_count}):{' '}
                      {preview.delete_sample.join('  ·  ')}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">Recorded in the audit log; cannot be undone in the app.</Text>
                </>
              ) : confirm === 'overwrite' ? (
                <Text size="sm">
                  Proceeding will <b>overwrite {preview.conflict_count} existing fee-month{plural(preview.conflict_count)}</b>
                  {preview.new_count > 0 && <> and import <b>{preview.new_count} new</b></>} across the matched agreements.
                  The previous values are replaced and cannot be recovered.
                </Text>
              ) : (
                <Text size="sm">
                  Import <b>{preview.new_count} new fee-month{plural(preview.new_count)}</b> across the matched agreements
                  {preview.conflict_count > 0 && <>; the {preview.conflict_count} already on record are kept unchanged</>}.
                </Text>
              )}
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setConfirm(null)}>Cancel</Button>
                <Button
                  color={confirm === 'replace' ? 'red' : confirm === 'overwrite' ? 'orange' : undefined}
                  loading={committing} onClick={() => handleCommit(confirm)}
                >
                  {confirm === 'replace' ? `Delete ${preview.delete_count} & replace`
                    : confirm === 'overwrite' ? 'Overwrite' : 'Import'}
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Stack>
    </Card>
  );
}

/** Conflict-aware confirm: no rows → nothing to import; new only → one Import; conflicts →
 *  Overwrite / Import-new-keep / Cancel. Mirrors the bulk-upload policy. */
function ConfirmActions({ report, committing, onConfirm, onCancel }: {
  report: MetricsImportReport;
  committing: boolean;
  onConfirm: (c: MetricsImportConflict) => void;
  onCancel: () => void;
}) {
  const del = report.delete_count ?? 0;
  // Offered whenever there are stored months not in the file (the confirm modal warns).
  const replaceBtn = del > 0 ? (
    <Tooltip
      label={`Makes your file the COMPLETE record for these agreements: overwrites/adds its months and permanently DELETES the ${del} stored month(s) not in the file (audited; cannot be undone in-app).`}
      multiline w={320} withArrow position="top"
    >
      <Button color="red" variant="outline" onClick={() => onConfirm('replace')} disabled={committing}>
        Replace history (delete {del})
      </Button>
    </Tooltip>
  ) : null;

  if (report.new_count === 0 && report.conflict_count === 0) {
    return (
      <Group>
        <Text size="sm" c="dimmed">Nothing to import from this file.</Text>
        <Button variant="default" onClick={onCancel}>Start over</Button>
      </Group>
    );
  }
  if (report.conflict_count === 0) {
    return (
      <Group>
        <Tooltip label="Adds these new months; nothing already stored is affected." multiline w={300} withArrow position="top">
          <Button onClick={() => onConfirm('skip')} loading={committing}>
            Import {report.new_count} new month{plural(report.new_count)}
          </Button>
        </Tooltip>
        {replaceBtn}
        <Button variant="default" onClick={onCancel} disabled={committing}>Cancel</Button>
      </Group>
    );
  }
  return (
    <Stack gap="xs">
      <Text size="sm">
        {report.conflict_count} month{plural(report.conflict_count)} already exist. Choose how to proceed:
      </Text>
      <Group>
        <Tooltip label="Updates the months in your file that already exist; other stored months are left untouched." multiline w={300} withArrow position="top">
          <Button color="orange" onClick={() => onConfirm('overwrite')} loading={committing}>Overwrite existing months</Button>
        </Tooltip>
        <Tooltip label="Adds only months not yet stored; existing months keep their current values." multiline w={300} withArrow position="top">
          <Button onClick={() => onConfirm('skip')} loading={committing}>Import new, keep existing</Button>
        </Tooltip>
        {replaceBtn}
        <Button variant="default" onClick={onCancel} disabled={committing}>Cancel</Button>
      </Group>
    </Stack>
  );
}
